import {
  LaftBannerColumn,
  LaftStatusBannerState,
} from '../services/laft-status-banner.service';
import {
  preApprovalBannerLimitSentence,
  uninvestedBalanceSentence,
} from './deposit-limit.messages';

export type LaftStatusChipMarker =
  | 'pending'
  | 'waiting'
  | 'current'
  | 'done'
  | 'error'
  | 'locked';

export interface LaftStatusBannerChip {
  label: string;
  marker: LaftStatusChipMarker;
}

export type LaftStatusBannerVariant =
  | 'default'
  | 'warning'
  | 'muted'
  | 'success'
  | 'error'
  | 'spinner';

export interface LaftStatusBannerCopy {
  title: string;
  description: string | null;
  chips: LaftStatusBannerChip[];
  buttonLabel: string | null;
  secondaryButtonLabel?: string;
  icon: string;
  variant: LaftStatusBannerVariant;
  dismissible: boolean;
}

const CHIP = {
  validationInProgress: 'Validación en curso',
  profileApproved: 'Perfil aprobado',
  contractUnsigned: 'Contrato sin firmar',
  contractSigned: 'Contrato firmado',
  signatureError: 'Firma con error',
  noDeposit: 'Sin depósito',
  depositDone: 'Depósito hecho',
  depositBlocked: 'Depósito bloqueado',
};

const BUTTON = {
  firstDeposit: 'Hacer mi primer depósito',
  signContract: 'Firmar mi contrato',
  signAgain: 'Volver a firmar',
  goToSign: 'Ir a firmar',
  retrySignature: 'Reintentar firma',
};

const FIRST_DEPOSIT_TITLE = 'Ya puedes hacer tu primer depósito';
const PROCESSING_DEPOSIT_TITLE = 'Estamos procesando tu depósito';

const SIGN_TO_BE_READY =
  'Firma tu contrato y quedas listo para invertir apenas terminemos de validar tu perfil.';

export const laftStatusBannerCopy = (
  state: LaftStatusBannerState,
  profile: any,
  validationDone = false,
  column: LaftBannerColumn | null = 'preApproved',
  contractSigned = true,
): LaftStatusBannerCopy => {
  const limitSentence = preApprovalBannerLimitSentence(profile?.depositAllowance ?? null);
  const balanceSentence = uninvestedBalanceSentence(
    profile?.availableBalancePEN,
    profile?.availableBalanceUSD,
  );

  const validationChip: LaftStatusBannerChip = validationDone
    ? { label: CHIP.profileApproved, marker: 'done' }
    : { label: CHIP.validationInProgress, marker: 'waiting' };
  const depositChip: LaftStatusBannerChip =
    profile?.hasCountedDeposit === true
      ? { label: CHIP.depositDone, marker: 'done' }
      : { label: CHIP.noDeposit, marker: 'pending' };

  const depositBlockedChip: LaftStatusBannerChip = {
    label: CHIP.depositBlocked,
    marker: 'locked',
  };

  // `depositPendingContractSigned` is reached both ways — signed and awaiting validation,
  // and approved without ever signing — so its contract chip cannot be a constant.
  const contractChip: LaftStatusBannerChip = contractSigned
    ? { label: CHIP.contractSigned, marker: 'done' }
    : { label: CHIP.contractUnsigned, marker: 'pending' };

  const settledDepositChip: LaftStatusBannerChip =
    column === 'noPreApproval'
      ? depositBlockedChip
      : { label: CHIP.depositDone, marker: 'done' };

  const copy: Record<LaftStatusBannerState, LaftStatusBannerCopy> = {
    firstDepositPending: {
      title: FIRST_DEPOSIT_TITLE,
      description: limitSentence,
      chips: [
        validationChip,
        { label: CHIP.contractUnsigned, marker: 'pending' },
        { label: CHIP.noDeposit, marker: 'current' },
      ],
      buttonLabel: BUTTON.firstDeposit,
      secondaryButtonLabel: BUTTON.goToSign,
      icon: 'security-user',
      variant: 'default',
      dismissible: false,
    },

    firstDepositSignatureError: {
      title: FIRST_DEPOSIT_TITLE,
      description: limitSentence,
      chips: [
        validationChip,
        { label: CHIP.signatureError, marker: 'error' },
        { label: CHIP.noDeposit, marker: 'current' },
      ],
      buttonLabel: BUTTON.firstDeposit,
      secondaryButtonLabel: BUTTON.retrySignature,
      icon: 'security-user',
      variant: 'default',
      dismissible: false,
    },

    contractPendingDepositApproved: {
      title: balanceSentence ?? FIRST_DEPOSIT_TITLE,
      description: balanceSentence ? SIGN_TO_BE_READY : limitSentence,
      chips: [
        validationChip,
        { label: CHIP.contractUnsigned, marker: 'current' },
        { label: CHIP.depositDone, marker: 'done' },
      ],
      buttonLabel: BUTTON.signContract,
      icon: 'document-text',
      variant: 'warning',
      dismissible: false,
    },

    contractPendingDepositInProcess: {
      title: PROCESSING_DEPOSIT_TITLE,
      description: SIGN_TO_BE_READY,
      chips: [
        validationChip,
        { label: CHIP.contractUnsigned, marker: 'current' },
        { label: CHIP.depositDone, marker: 'done' },
      ],
      buttonLabel: BUTTON.signContract,
      icon: 'document-text',
      variant: 'warning',
      dismissible: false,
    },

    depositPendingContractSigned: {
      title: FIRST_DEPOSIT_TITLE,
      description: limitSentence,
      chips: [
        validationChip,
        contractChip,
        { label: CHIP.noDeposit, marker: 'current' },
      ],
      buttonLabel: BUTTON.firstDeposit,
      icon: 'security-user',
      variant: 'default',
      dismissible: false,
    },

    validationPending: {
      title: 'Ya está todo listo de tu lado',
      description:
        'Falta que terminemos de validar tu perfil para que puedas invertir. Te avisamos por correo apenas esté.',
      chips: [
        validationChip,
        { label: CHIP.contractSigned, marker: 'done' },
        settledDepositChip,
      ],
      buttonLabel: null,
      icon: 'clock-gray',
      variant: 'muted',
      dismissible: false,
    },

    depositPendingProfileApproved: {
      title: 'Ya puedes invertir, solo falta que deposites',
      description:
        'Tu perfil está aprobado y tu contrato firmado. Deposita y empieza a invertir hoy mismo.',
      chips: [
        validationChip,
        { label: CHIP.contractSigned, marker: 'done' },
        { label: CHIP.noDeposit, marker: 'current' },
      ],
      buttonLabel: BUTTON.firstDeposit,
      icon: 'security-user',
      variant: 'default',
      dismissible: false,
    },

    depositInProcessContractSigned: {
      title: PROCESSING_DEPOSIT_TITLE,
      description: 'Te enviaremos un correo cuando tu depósito esté aprobado.',
      chips: [],
      buttonLabel: null,
      icon: 'clock-gray',
      variant: 'muted',
      dismissible: false,
    },

    readyToInvest: {
      title: 'Ya puedes invertir',
      description:
        'Tu perfil está aprobado y tu contrato firmado. Empieza a invertir hoy mismo.',
      chips: [],
      buttonLabel: null,
      icon: 'tick-circle-green',
      variant: 'success',
      dismissible: true,
    },

    signatureError: {
      title: 'No pudimos procesar tu firma',
      description:
        'Vuelve a intentarlo cuidando que tu rostro y tu documento se vean bien en la cámara.',
      chips: [
        validationChip,
        { label: CHIP.signatureError, marker: 'error' },
        depositChip,
      ],
      buttonLabel: BUTTON.signAgain,
      icon: 'warning-2',
      variant: 'error',
      dismissible: false,
    },

    contractPendingValidationInProgress: {
      title: 'Estamos validando tu perfil',
      description:
        'Toma hasta 12 horas. Mientras tanto puedes dejar firmado tu contrato y adelantar un paso.',
      chips: [
        validationChip,
        { label: CHIP.contractUnsigned, marker: 'current' },
        depositBlockedChip,
      ],
      buttonLabel: BUTTON.signContract,
      icon: 'clock-gray',
      variant: 'muted',
      dismissible: false,
    },

    contractPendingValidationDone: {
      title: 'Para depositar solo falta que firmes el contrato',
      description:
        'Tu perfil está aprobado, solo necesitas firmar para que empieces a invertir hoy mismo.',
      chips: [
        validationChip,
        // `pending`, not `current`, although the button acts on it: the frame draws this
        // chip hollow-grey here and blue on the 1st banner (D126).
        { label: CHIP.contractUnsigned, marker: 'pending' },
        depositBlockedChip,
      ],
      buttonLabel: BUTTON.signContract,
      icon: 'security-user',
      variant: 'default',
      dismissible: false,
    },

    signatureValidating: {
      title: 'Validando la firma al Contrato y Declaración Jurada',
      description: 'Este proceso puede tomar unos segundos.',
      chips: [],
      buttonLabel: null,
      icon: 'loading-spinner',
      variant: 'spinner',
      dismissible: false,
    },
  };

  return copy[state];
};
